import type { AiCall } from "@pax/api-contract/ai/calls/v1alpha1";
import type { StreamChunk } from "@pax/api-contract/ai/shared";
import { cn } from "@pax/ui";
import { useLayoutEffect, useRef, useState } from "react";
import { formatDuration, plural } from "@/features/evals/components";
import { EventTimeline, type TimelineEvent } from "./EventTimeline";
import { firstChunkAt, modelOf } from "./model-call";

type PhaseKind = "waiting" | "reasoning" | "text" | "tool";

interface Phase {
  kind: PhaseKind;
  label: string;
  chunks: number;
  startMs: number;
  endMs: number;
  /** Placed by where its chunks fall in the stream rather than by a recorded time. */
  estimated: boolean;
}

const COLOR: Record<PhaseKind, string> = {
  waiting: "bg-foreground/20",
  reasoning: "bg-violet-500",
  text: "bg-emerald-500",
  tool: "bg-sky-500",
};

const CHUNK_FILL: Record<StreamChunk["kind"], string> = {
  reasoning: "fill-violet-500",
  text: "fill-emerald-500",
  tool: "fill-sky-500",
  usage: "fill-amber-500",
  finish: "fill-foreground/60",
  other: "fill-foreground/35",
};

const CHUNK_LABEL: Record<StreamChunk["kind"], string> = {
  reasoning: "Reasoning chunk",
  text: "Text chunk",
  tool: "Tool call chunk",
  usage: "Usage arrived",
  finish: "Finish reason",
  other: "Chunk",
};

/**
 * Where one call's time went: waiting for the first chunk, then each stretch of the stream in the
 * order it arrived. Calls recorded before each chunk was timed have only the first chunk and the
 * end, so their stream is spread evenly across its chunks.
 */
function readCallTime(
  call: AiCall,
  chunks: readonly StreamChunk[],
  chunkOffsetsMs: readonly number[],
  reasoningHidden: boolean,
) {
  const totalMs = Date.parse(call.endedAt) - Date.parse(call.receivedAt);
  const firstChunk = firstChunkAt(call);
  if (!firstChunk) {
    const whole: Phase = {
      kind: "waiting",
      label: "Waiting for the whole response",
      chunks: 0,
      startMs: 0,
      endMs: totalMs,
      estimated: false,
    };
    return { totalMs, phases: [whole], arrivals: chunks.map(() => totalMs), spread: false };
  }
  const firstChunkMs = Date.parse(firstChunk) - Date.parse(call.receivedAt);
  const streamMs = totalMs - firstChunkMs;
  const timed = chunks.length > 0 && chunkOffsetsMs.length === chunks.length;
  const arrivals = timed
    ? [...chunkOffsetsMs]
    : chunks.map(
        (_, index) =>
          firstChunkMs + (chunks.length > 1 ? (index / (chunks.length - 1)) * streamMs : 0),
      );

  const runs: { chunk: StreamChunk; firstIndex: number; chunks: number }[] = [];
  chunks.forEach((chunk, index) => {
    if (chunk.kind !== "reasoning" && chunk.kind !== "text" && chunk.kind !== "tool") return;
    if (chunk.kind === "reasoning" && reasoningHidden) return;
    const last = runs.at(-1);
    if (last && sameStretch(last.chunk, chunk)) last.chunks += 1;
    else runs.push({ chunk, firstIndex: index, chunks: 1 });
  });

  const waiting: Phase = {
    kind: "waiting",
    label: reasoningHidden ? "Waiting, while it reasoned unseen" : "Waiting for the first chunk",
    chunks: 0,
    startMs: 0,
    endMs: firstChunkMs,
    estimated: false,
  };
  const stretches = runs.map(
    ({ chunk, firstIndex, chunks: count }, index): Phase => ({
      kind: chunk.kind as PhaseKind,
      label:
        chunk.kind === "tool"
          ? `Calling ${chunk.tool}`
          : chunk.kind === "text"
            ? "Writing the reply"
            : "Reasoning summary",
      chunks: count,
      startMs: index === 0 ? firstChunkMs : (arrivals[firstIndex] ?? firstChunkMs),
      endMs:
        index === runs.length - 1
          ? totalMs
          : (arrivals[runs[index + 1]?.firstIndex ?? 0] ?? totalMs),
      estimated: !timed && runs.length > 1,
    }),
  );
  return {
    totalMs,
    phases: [waiting, ...stretches],
    arrivals,
    spread: !timed && chunks.length > 2,
  };
}

const sameStretch = (left: StreamChunk, right: StreamChunk) =>
  left.kind === right.kind &&
  (left.kind !== "tool" || (right.kind === "tool" && left.tool === right.tool));

/**
 * The call on a zoomable line of time: where its time went, shaded behind every chunk it streamed,
 * with each stretch's share beneath. Pressing a chunk shows it exactly as it arrived.
 */
export function CallTimeline({
  call,
  chunks,
  chunkOffsetsMs,
  rawChunks,
  reasoningHidden,
}: {
  call: AiCall;
  chunks: readonly StreamChunk[];
  /** When each chunk arrived, in milliseconds from the request; empty for calls never timed so. */
  chunkOffsetsMs: readonly number[];
  /** Each chunk as the provider sent it, in the same order as `chunks`. */
  rawChunks: readonly unknown[];
  reasoningHidden: boolean;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const { totalMs, phases, arrivals, spread } = readCallTime(
    call,
    chunks,
    chunkOffsetsMs,
    reasoningHidden,
  );
  const firstByteMs = call.timing.firstByteMs;
  const share = (phase: Phase) => (totalMs ? (phase.endMs - phase.startMs) / totalMs : 0);
  const events: TimelineEvent[] = [
    {
      atMs: 0,
      fill: "fill-foreground",
      label: "Request sent",
      detail: { receivedAt: call.receivedAt, model: modelOf(call), inputTokens: call.inputTokens },
    },
    ...(firstByteMs && firstByteMs < (arrivals[0] ?? totalMs)
      ? [
          {
            atMs: firstByteMs,
            fill: "fill-foreground/35",
            label: "First byte, a keep-alive",
            detail: { firstByteMs },
          },
        ]
      : []),
    ...chunks.map(
      (chunk, index): TimelineEvent => ({
        atMs: arrivals[index] ?? 0,
        fill: CHUNK_FILL[chunk.kind],
        label:
          chunk.kind === "tool" ? `${CHUNK_LABEL.tool} · ${chunk.tool}` : CHUNK_LABEL[chunk.kind],
        estimated: spread && index > 0,
        detail: rawChunks[index],
        rendered: streamOf(chunk) ? <ChunkInContext chunks={chunks} index={index} /> : undefined,
      }),
    ),
    {
      atMs: totalMs,
      fill: "fill-foreground",
      label: "Stream closed",
      detail: { endedAt: call.endedAt, status: call.status, outputTokens: call.outputTokens },
    },
  ];
  return (
    <section className="mt-7">
      <p className="mb-2 text-xs text-muted-foreground">Timeline</p>
      <EventTimeline
        totalMs={totalMs}
        bands={phases.map((phase, index) => ({
          startMs: phase.startMs,
          endMs: phase.endMs,
          tone: cn(COLOR[phase.kind], hovered !== null && hovered !== index && "opacity-40"),
          striped: phase.estimated,
        }))}
        events={events}
      />
      <ul className="mt-3 space-y-0.5">
        {phases.map((phase, index) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: phases keep their order
            key={index}
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
            className={cn(
              "-mx-2 grid grid-cols-[auto_minmax(0,1fr)_4.5rem_3rem] items-center gap-3 rounded-lg px-2 py-1 transition-colors",
              hovered === index && "bg-foreground/5",
            )}
          >
            <span className={cn("size-2 rounded-full", COLOR[phase.kind])} />
            <span className="truncate">
              {phase.label}
              {phase.chunks > 0 && (
                <span className="ml-2 text-xs text-muted-foreground">
                  {plural(phase.chunks, "chunk")}
                </span>
              )}
            </span>
            <span className="text-right text-muted-foreground tabular-nums">
              {phase.estimated && "≈"}
              {formatDuration(phase.endMs - phase.startMs)}
            </span>
            <span className="text-right tabular-nums">{percent(share(phase))}</span>
          </li>
        ))}
      </ul>
      {spread && (
        <p className="mt-3 text-xs leading-5 text-muted-foreground">
          Only the first chunk and the end are timed, so the chunks between are spread evenly across
          the stream, and stretches with more than one kind of chunk are striped.
        </p>
      )}
    </section>
  );
}

/** Which stream of the response a chunk adds to: the reasoning, the reply, or one tool call. */
function streamOf(chunk: StreamChunk): string | undefined {
  if (chunk.kind === "tool") return `tool:${chunk.call}`;
  if (chunk.kind === "reasoning" || chunk.kind === "text") return chunk.kind;
  return undefined;
}

const textOf = (chunk: StreamChunk) => ("text" in chunk ? chunk.text : "");

const MARK: Record<"reasoning" | "text" | "tool", string> = {
  reasoning: "bg-violet-500/30 ring-violet-500/60",
  text: "bg-emerald-500/30 ring-emerald-500/60",
  tool: "bg-sky-500/30 ring-sky-500/60",
};

/**
 * A chunk inside the stream it added to: what had arrived before it, what it added marked, and
 * what was still to come faded.
 */
function ChunkInContext({ chunks, index }: { chunks: readonly StreamChunk[]; index: number }) {
  const chunk = chunks[index];
  const stream = chunk && streamOf(chunk);
  const box = useRef<HTMLDivElement>(null);
  const mark = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (!box.current || !mark.current) return;
    box.current.scrollTop =
      mark.current.offsetTop - box.current.clientHeight / 2 + mark.current.offsetHeight / 2;
  }, [index]);
  if (
    !chunk ||
    !stream ||
    (chunk.kind !== "reasoning" && chunk.kind !== "text" && chunk.kind !== "tool")
  )
    return null;
  const inStream = chunks.flatMap((candidate, at) =>
    streamOf(candidate) === stream ? [{ at, text: textOf(candidate) }] : [],
  );
  const before = inStream
    .filter(item => item.at < index)
    .map(item => item.text)
    .join("");
  const after = inStream
    .filter(item => item.at > index)
    .map(item => item.text)
    .join("");
  const added = chunk.text;
  const place = inStream.findIndex(item => item.at === index) + 1;
  const title =
    chunk.kind === "tool"
      ? `Arguments to ${chunk.tool}`
      : chunk.kind === "text"
        ? "The reply"
        : "Reasoning summary";
  return (
    <div className="rounded-xl bg-foreground/[0.03]">
      <div className="flex items-center justify-between gap-3 px-3 pt-2.5 pb-1.5 text-[11px] text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className={cn("size-2 shrink-0 rounded-full", COLOR[chunk.kind])} />
          <span className="truncate">{title}</span>
        </span>
        <span className="shrink-0 tabular-nums">
          Chunk {place} of {inStream.length} ·{" "}
          {Math.round(
            ((before.length + added.length) / Math.max(1, (before + added + after).length)) * 100,
          )}
          % written
        </span>
      </div>
      <div
        ref={box}
        className={cn(
          "relative max-h-80 overflow-y-auto px-3 pb-3 leading-6 break-words whitespace-pre-wrap",
          chunk.kind === "tool" ? "font-mono text-xs" : "text-sm",
        )}
      >
        <span className="text-foreground/85">{before}</span>
        <mark
          ref={mark}
          className={cn("rounded-sm px-px text-foreground ring-1", MARK[chunk.kind])}
        >
          {added || "∅"}
        </mark>
        <span className="text-muted-foreground/45">{after}</span>
      </div>
      {!added && (
        <p className="px-3 pb-2.5 text-[11px] text-muted-foreground">
          This chunk added no readable text; switch to Raw to see what it carried.
        </p>
      )}
    </div>
  );
}

function percent(fraction: number) {
  if (fraction > 0 && fraction < 0.01) return "<1%";
  return `${Math.round(fraction * 100)}%`;
}
